import { TandemTool } from "../../base/TandemTool.js";
import { logger } from "../../../../../../core/logger.js";

export class WriteFileTool extends TandemTool {
  constructor() {
    super();
    this.toolName = "writeFileTool";
  }

  /**
   * @param {string} path
   * @param {string} content
   * @param {boolean} [overwrite=true]
   * @param {string} [encoding='utf8'] - utf8 | base64
   * @param {boolean} [createDirectories=true]
   * @returns {Promise<Object>}
   */
  async writeFile(
    path,
    content,
    overwrite = true,
    encoding = "utf8",
    createDirectories = true
  ) {
    try {
      if (!path || typeof path !== "string") {
        return this.returnError({}, "Valid file path required");
      }
      if (content === undefined || content === null) {
        return this.returnError({}, "Content is required");
      }

      const api = this.getTandemLocalAppApi();
      if (!api?.writeFile) {
        return this.returnError(
          {},
          "Tandem file writes require Revolt Desktop (tandemAPI.writeFile)."
        );
      }

      const root = this.getTandemWorkingDirectoryRoot();
      if (!root) {
        return this.returnError(
          {},
          "Tandem has no project folder set (working directory path)."
        );
      }

      const normalizedPath = this.normalizeAndValidatePath(path);
      if (!normalizedPath) {
        return this.returnError({}, "Invalid file path");
      }
      const relativePath = normalizedPath.replace(/^\/+/, "");

      let fileExists = false;
      if (api.getPathInfo) {
        const info = await api.getPathInfo({
          rootPath: root,
          relativePath,
        });
        if (!info?.success) {
          return this.returnError(
            {},
            info?.error || "Failed to inspect path on disk"
          );
        }
        fileExists = !!info.exists;
        if (info.exists && info.isDirectory) {
          return this.returnError({}, `Path is a directory: ${path}`);
        }
      }

      if (fileExists && !overwrite) {
        return this.returnError(
          {},
          `File already exists and overwrite is disabled: ${path}`
        );
      }

      if (createDirectories === false) {
        const parentRel = relativePath.includes("/")
          ? relativePath.replace(/\/[^/]+$/, "")
          : "";
        if (parentRel && api.getPathInfo) {
          const parentInfo = await api.getPathInfo({
            rootPath: root,
            relativePath: parentRel,
          });
          if (!parentInfo?.success || !parentInfo.exists) {
            return this.returnError(
              {},
              "Parent directory does not exist (createDirectories is false)"
            );
          }
        }
      }

      let enc = "utf8";
      let contentPayload = content;
      if (encoding === "base64") {
        enc = "base64";
        if (typeof content !== "string") {
          return this.returnError({}, "base64 content must be a string");
        }
      } else if (typeof content !== "string") {
        return this.returnError({}, "Content must be a string for utf8 encoding");
      }

      const res = await api.writeFile({
        rootPath: root,
        relativePath,
        content: contentPayload,
        encoding: enc,
      });

      if (!res?.success) {
        return this.returnError({}, res?.error || "Failed to write file on disk");
      }

      const action = fileExists ? "updated" : "created";
      logger.log(
        { tags: "tools|tandem|writefile|info", color1: "lime" },
        `Tandem file ${action}: ${normalizedPath}`,
        { size: res.size }
      );

      return this.returnSuccess(
        {
          path: normalizedPath,
          action,
          size: res.size ?? null,
        },
        `File ${action} successfully`
      );
    } catch (error) {
      logger.error(
        { tags: "tools|tandem|writefile|error", color1: "red" },
        `Tandem failed to write file: ${path}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }
}
